import React, { useMemo } from 'react';
import { Button } from '@mui/material';
import { useAtomValue } from 'jotai';
import {
    eachConditionDataByKeyAtom,
    EachConditionDataKeys
} from 'atoms/Rtool/cabinAtoms';
import { conditionIdAtom, stageConfigAtom } from 'atoms/Rtool/stageConfigAtoms';
import { buildCalcLabel } from 'atoms/recentCalcsAtom';
import { getCsvColumnOrder } from 'utils/charts/csvColumnOrder';
import { buildCsvBlob, buildCsvMetadataLines } from 'utils/charts/csvMetadata';

interface DownloadCsvButtonProps {
    /** The key identifying which dataset to download */
    datasetKey: EachConditionDataKeys;
}

export const DownloadCsvButton: React.FC<DownloadCsvButtonProps> = ({ datasetKey }) => {
    const conditionAtom = useMemo(
        () => eachConditionDataByKeyAtom(datasetKey),
        [datasetKey]
    );
    const { data, status } = useAtomValue(conditionAtom) ?? { data: {}, status: 'notstarted' };
    const conditionId = useAtomValue(conditionIdAtom);
    const config = useAtomValue(stageConfigAtom);

    const handleDownload = () => {
        if (status !== 'success') return;

        // Assume `data` is { columnName: number[] }
        const dataObj = data as Record<string, Array<number | null>>;
        const valueHeaders = getCsvColumnOrder(datasetKey, dataObj);
        if (valueHeaders.length === 0) return;

        // Prepare headers with an index column in front
        const headers = ['frequency', ...valueHeaders];

        // Determine the longest column length
        const maxLen = Math.max(...valueHeaders.map(h => dataObj[h]?.length ?? 0));

        // Build rows with index as the first cell
        const rows: string[][] = [];
        for (let i = 0; i < maxLen; i++) {
            rows.push([
                String(i + 1),
                ...valueHeaders.map(h => {
                    const val = dataObj[h]?.[i];
                    return val != null ? String(val) : '';
                })
            ]);
        }

        // 冒頭に計算セット名・ダウンロード日時を付与してから、ヘッダ + 行を続ける。
        const metaLines = buildCsvMetadataLines({
            calcSetName: buildCalcLabel(config),
            date: new Date(),
        });
        const csvContent = [
            ...metaLines,
            headers.join(','),
            ...rows.map(r => r.join(','))
        ].join('\r\n');

        // ファイル名は condition_id（現象-運転モード）を接頭辞に付ける。未選択時は datasetKey のみ。
        const fileName = conditionId ? `${conditionId}_${datasetKey}.csv` : `${datasetKey}.csv`;

        // Trigger download（Excel 文字化け防止のため UTF-8 BOM 付き Blob を使う）
        const blob = buildCsvBlob(csvContent);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <Button
            variant="outlined"
            size="small"
            onClick={handleDownload}
            disabled={status !== 'success'}
        >
            CSV ダウンロード
        </Button>
    );
};
